import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();
const TOKEN_KEY = 'GYD_TOKEN';
const USER_KEY = 'GYD_USER';
const TOKEN_EXPIRY_KEY = 'GYD_TOKEN_EXPIRY';
// 30 days in milliseconds
const EXPIRATION_TIME = 30 * 24 * 60 * 60 * 1000;

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(localStorage.getItem(TOKEN_KEY));
  const [currentUser, setCurrentUser] = useState(JSON.parse(localStorage.getItem(USER_KEY) || 'null'));

  // Check if token is expired on initial load
  useEffect(() => {
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (expiry && new Date().getTime() > parseInt(expiry, 10)) {
      // Token expired, clear it
      clearToken();
    }
  }, []);

  function setToken(tok) {
    if (tok) {
      const expiry = new Date().getTime() + EXPIRATION_TIME;
      localStorage.setItem(TOKEN_KEY, tok);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
      // Also set it as a cookie for cross-tab persistence
      document.cookie = `${TOKEN_KEY}=${tok};path=/;max-age=${EXPIRATION_TIME / 1000};SameSite=Strict`;
      setTokenState(tok);
    } else {
      clearToken();
    }
  }
  
  function setUser(user) {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_KEY);
    }
    setCurrentUser(user);
  }
  
  function clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(USER_KEY);
    // Clear the cookie as well
    document.cookie = `${TOKEN_KEY}=;path=/;max-age=0;SameSite=Strict`;
    setTokenState(null);
    setCurrentUser(null);
  }
  
  async function login(username, password) {
    const { data } = await api.post('token/', { username, password });
    setToken(data.access);
    
    // Fetch user profile after login
    try {
      const { data: userData } = await api.get('users/me/');
      setUser(userData);
      return userData; // Return the user data for redirect purposes
    } catch (err) {
      console.error('Failed to fetch user profile after login:', err);
      throw err;
    }
  }
  
  // Fetch user profile when token changes/on initial load
  useEffect(() => {
    if (token && !currentUser) {
      api.get('users/me/')
        .then(({ data }) => setUser(data))
        .catch(() => {
          // If fetching user fails, token might be invalid
          if (token) clearToken();
        });
    }
  }, [token]);
  
  return (
    <AuthContext.Provider value={{ 
      token, 
      login, 
      logout: clearToken, 
      currentUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  document.cookie = `${TOKEN_KEY}=;path=/;max-age=0;SameSite=Strict`;
}